import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchProjects } from "../../Redux/WhatsappSlice/Project";
import { ProjWiseClient } from "../../Redux/Slice/DbExeUpdate";
import {
    FormControl,
    InputLabel,
    ListItemText,
    MenuItem,
    OutlinedInput,
    Select,
    Checkbox,
    CircularProgress,
    Box,
    IconButton,
} from "@mui/material";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Loader from "../../Components/Loader/Loader";
import DynamicTable from "../../Components/Table/DynamicTable";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";

const ExelUpdate = () => {
    const dispatch = useDispatch();

    const { ProjectData, loading: projectsLoading } = useSelector(
        (state) => state.Project
    );

    const {
        projWiseClients,
        projWiseClientsLoading,
        projWiseClientsError,
    } = useSelector((state) => state.DbExeUpdate);

    const [notifyFormData, setNotifyFormData] = useState({
        clientids: [],
        projectid: "",
    });

    const [uploadFormData, setUploadFormData] = useState({
        uploadFile: null,
        exelName: "",
        exelId: "",
    });

    const [exelData, setExelData] = useState([]);
    const [editId, setEditId] = useState(null);

    const [uploadLoading, setUploadLoading] = useState(false);

    const [clients, setClients] = useState([]);
    const [isInitialLoading, setIsInitialLoading] = useState(true);

    const [openProjectSelect, setOpenProjectSelect] = useState(false);
    const [openClientSelect, setOpenClientSelect] = useState(false);

    useEffect(() => {
        const loadData = async () => {
            setIsInitialLoading(true);
            try {
                await dispatch(fetchProjects());
                fetchExelData();
            } finally {
                setIsInitialLoading(false);
            }
        };
        loadData();
    }, [dispatch]);

    const fetchExelData = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/DBsync/GetExelData`);
            const result = await response.json();
            if (response.ok && result.success) {
                setExelData(result.data);
            }
        } catch (error) {
            console.error("Failed to fetch exel data", error);
        }
    };

    // Auto-generate EXEL ID when exelData is loaded or when not editing
    useEffect(() => {
        if (!editId) {
            if (exelData.length > 0) {
                let maxId = 0;
                exelData.forEach(row => {
                    const idNum = parseInt(row.EXELID, 10);
                    if (!isNaN(idNum) && idNum > maxId) {
                        maxId = idNum;
                    }
                });
                const nextId = String(maxId + 1).padStart(4, '0');
                setUploadFormData(prev => ({ ...prev, exelId: nextId }));
            } else {
                setUploadFormData(prev => ({ ...prev, exelId: "0001" }));
            }
        }
    }, [exelData, editId]);

    useEffect(() => {
        if (notifyFormData.projectid) {
            dispatch(ProjWiseClient(notifyFormData.projectid));
        } else {
            setClients([]);
            // Don't wipe clientids here to prevent bugs during handleEdit when project is temporarily empty
        }
    }, [notifyFormData.projectid, dispatch]);

    useEffect(() => {
        if (projWiseClientsLoading) return;

        if (!projWiseClients) {
            setClients([]);
            return;
        }

        if (projWiseClients?.status === "Fail") {
            setClients([]);
            return;
        }

        const clientList = Array.isArray(projWiseClients)
            ? projWiseClients
            : Array.isArray(projWiseClients?.data)
                ? projWiseClients.data
                : [];

        const processed = clientList.map((client) => ({
            id: String(client._id || client.id || client.clientId || ""),
            name: client.WhaClientName || client.name || client.clientName || "Unnamed Client",
        }));

        processed.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));

        setClients(processed);
    }, [projWiseClients, projWiseClientsLoading]);

    const menuProps = {
        PaperProps: {
            style: {
                maxHeight: 300,
                width: 350,
            },
        },
        disablePortal: true,
    };

    const handleNotifyProjectChange = (event) => {
        const projectid = event.target.value;
        setNotifyFormData({
            clientids: [],
            projectid: projectid,
        });
        setClients([]);
    };

    const handleNotifyClientChange = (event) => {
        const { value } = event.target;

        if (value[value.length - 1] === "all") {
            const allSelected = notifyFormData.clientids.length === clients.length;
            const newValue = allSelected ? [] : clients.map((client) => client.id);
            setNotifyFormData((prev) => ({ ...prev, clientids: newValue }));
            return;
        }

        setNotifyFormData((prev) => ({ ...prev, clientids: value }));
    };

    const renderNotifyClientValue = (selected) => {
        if (!selected || selected.length === 0) {
            return "Select clients";
        }

        const selectedCount = selected.length;
        if (selectedCount > 3) {
            return `${selectedCount} clients selected`;
        }

        const names = selected
            .map((id) => {
                const client = clients.find((c) => c.id === id);
                return client ? client.name : "";
            })
            .filter((name) => name);

        return names.join(", ");
    };

    const renderClientMenuItems = (selectedIds) => {
        if (projWiseClientsLoading) {
            return (
                <MenuItem disabled>
                    <Box display="flex" alignItems="center" justifyContent="center" width="100%">
                        <CircularProgress size={20} />
                        <ListItemText primary="Loading clients..." style={{ marginLeft: 10 }} />
                    </Box>
                </MenuItem>
            );
        }

        if (!notifyFormData.projectid) {
            return (
                <MenuItem value="">
                    <ListItemText primary="No project chosen — showing no clients" />
                </MenuItem>
            );
        }

        if (projWiseClientsError) {
            return (
                <MenuItem disabled>
                    <ListItemText primary="Error loading clients" secondary={projWiseClientsError} />
                </MenuItem>
            );
        }

        if (!clients || clients.length === 0) {
            return (
                <MenuItem value="">
                    <ListItemText primary="No clients found for this project" />
                </MenuItem>
            );
        }

        return [
            <MenuItem key="select-all" value="all">
                <Checkbox
                    checked={clients.length > 0 && selectedIds.length === clients.length}
                    indeterminate={
                        selectedIds.length > 0 && selectedIds.length < clients.length
                    }
                />
                <ListItemText primary={`Select All (${clients.length})`} />
            </MenuItem>,
            ...(Array.isArray(clients) ? clients : []).map((client) => (
                <MenuItem key={client.id} value={client.id}>
                    <Checkbox checked={selectedIds.includes(client.id)} />
                    <ListItemText primary={client.name} />
                </MenuItem>
            )),
        ];
    };

    const renderProjectMenuItems = () => {
        if (projectsLoading) {
            return (
                <MenuItem disabled>
                    <Box display="flex" alignItems="center" justifyContent="center" width="100%">
                        <CircularProgress size={20} />
                        <ListItemText primary="Loading projects..." style={{ marginLeft: 10 }} />
                    </Box>
                </MenuItem>
            );
        }

        if (!ProjectData || !Array.isArray(ProjectData) || ProjectData.length === 0) {
            return <MenuItem value="">No projects available</MenuItem>;
        }

        const sortedProjects = [...ProjectData].sort((a, b) => {
            const nameA = (a.project_name || a.name || "Unnamed Project").toLowerCase();
            const nameB = (b.project_name || b.name || "Unnamed Project").toLowerCase();
            return nameA.localeCompare(nameB);
        });

        return [
            <MenuItem key="placeholder" value="" disabled>
                <em className="text-gray-400 font-normal">Select Project</em>
            </MenuItem>,
            ...sortedProjects.map((project) => (
                <MenuItem key={project._id || project.id} value={project._id || project.id}>
                    {project.project_name || project.name || "Unnamed Project"}
                </MenuItem>
            ))
        ];
    };

    const handleUploadExel = async () => {
        if (!uploadFormData.exelName || !uploadFormData.exelId || notifyFormData.clientids.length === 0) {
            toast.warning("Please fill all fields and select clients!");
            return;
        }

        setUploadLoading(true);

        const data = new FormData();
        if (uploadFormData.uploadFile) {
            data.append("exelFile", uploadFormData.uploadFile);
        }
        data.append("EXELNAME", uploadFormData.exelName);
        data.append("EXELID", uploadFormData.exelId);
        data.append("CLIENTID", JSON.stringify(notifyFormData.clientids));
        data.append("PROJECTID", notifyFormData.projectid);

        try {
            let url = `${process.env.REACT_APP_API_URL}/DBsync/UploadExel`;
            let method = "POST";
            if (editId) {
                url = `${process.env.REACT_APP_API_URL}/DBsync/UpdateExelData/${editId}`;
                method = "PUT";
            }

            const response = await fetch(url, {
                method: method,
                body: data,
            });

            const result = await response.json();
            if (response.ok && result.success) {
                toast.success(`EXEL ${editId ? "updated" : "uploaded"} successfully!`);
                setUploadFormData({
                    uploadFile: null,
                    exelName: "",
                    exelId: "",
                });

                const fileInput = document.getElementById("exel-file-input");
                if (fileInput) fileInput.value = "";

                setNotifyFormData({
                    clientids: [],
                    projectid: "",
                });
                setClients([]);
                setEditId(null);
                fetchExelData();
            } else {
                toast.error(result.message || "Upload failed");
            }
        } catch (error) {
            toast.error(error.message);
        } finally {
            setUploadLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this record?")) {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/DBsync/DeleteExelData/${id}`, {
                    method: "DELETE"
                });
                const result = await response.json();
                if (response.ok && result.success) {
                    toast.success("EXEL deleted successfully!");
                    fetchExelData();
                } else {
                    toast.error(result.message || "Failed to delete");
                }
            } catch (error) {
                toast.error(error.message);
            }
        }
    };

    const handleEdit = (row) => {
        setEditId(row._id);
        setUploadFormData({
            uploadFile: null,
            exelName: row.EXELNAME,
            exelId: row.EXELID,
        });

        const fileInput = document.getElementById("exel-file-input");
        if (fileInput) fileInput.value = "";

        const cIds = row.CLIENTID.map(c => c._id);

        let derivedProjectId = "";
        if (row.CLIENTID.length > 0 && row.CLIENTID[0].Project && row.CLIENTID[0].Project.length > 0) {
            derivedProjectId = row.CLIENTID[0].Project[0];
        }
        const finalProjectId = row.PROJECTID || derivedProjectId;

        setNotifyFormData(prev => ({ ...prev, clientids: cIds, projectid: finalProjectId }));

        const rowClients = row.CLIENTID.map(c => ({
            id: c._id,
            name: c.WhaClientName
        }));
        setClients(rowClients);

        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleCancel = () => {
        setUploadFormData({
            uploadFile: null,
            exelName: "",
            exelId: "",
        });

        const fileInput = document.getElementById("exel-file-input");
        if (fileInput) fileInput.value = "";

        setNotifyFormData({
            clientids: [],
            projectid: "",
        });
        setClients([]);
        setEditId(null);
    };

    const columns = [
        { field: "id", headerName: "Sr. No", width: 90 },
        { field: "EXELNAME", headerName: "EXEL Name", flex: 1 },
        { field: "EXELID", headerName: "EXEL ID", flex: 1 },
        {
            field: "CLIENTS",
            headerName: "Clients",
            flex: 2,
            renderCell: (params) => (
                <div className="max-h-20 overflow-y-auto whitespace-normal break-words w-full">
                    {params.value}
                </div>
            )
        },
        {
            field: "EXELURL",
            headerName: "EXEL URL",
            flex: 1,
            renderCell: (params) => (
                <a href={params.value} target="_blank" rel="noreferrer" className="py-2 px-4 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors inline-block text-center font-medium shadow-sm">
                    Download
                </a>
            ),
        },
        {
            field: "actions",
            headerName: "Actions",
            width: 160,
            renderCell: (params) => (
                <div className="flex space-x-2">
                    <button onClick={() => handleEdit(params.row.raw)} className="py-2 px-3 text-white rounded-md bg-[#122A47]">
                        Edit
                    </button>
                    <button onClick={() => handleDelete(params.row.raw._id)} className="py-2 px-3 text-white rounded-md bg-red-600">
                        Delete
                    </button>
                </div>
            ),
        },
    ];

    const rows = exelData.map((row, index) => ({
        id: index + 1,
        EXELNAME: row.EXELNAME,
        EXELID: row.EXELID,
        CLIENTS: row.CLIENTID.map(c => c.WhaClientName).join(", "),
        EXELURL: row.EXELURL,
        raw: row
    }));

    if (isInitialLoading || projectsLoading) {
        return <Loader />;
    }

    return (
        <div className="w-full p-4 space-y-6">

            <div className="rounded-xl p-6 shadow-lg bg-white">
                <div className="title mb-5">
                    <h3 className="text-xl font-bold main_title_page text-white px-4 py-2 inline-block rounded">
                        {editId ? "Update EXEL" : "Upload EXEL"}
                    </h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-start mb-6">
                    <div className="md:col-span-4">
                        <label className="font-semibold block mb-1">Select Project</label>
                        <FormControl fullWidth size="medium">
                            <Select
                                open={openProjectSelect}
                                displayEmpty
                                value={notifyFormData.projectid}
                                onChange={handleNotifyProjectChange}
                                onOpen={() => setOpenProjectSelect(true)}
                                onClose={() => setOpenProjectSelect(false)}
                                input={
                                    <OutlinedInput
                                        sx={{
                                            backgroundColor: "#F3F8FB",
                                            borderRadius: "8px",
                                            padding: "3px",
                                            "& fieldset": {
                                                borderColor: "black",
                                                borderWidth: "2px",
                                            },
                                            "&:hover fieldset": {
                                                borderColor: "black",
                                            },
                                            "&.Mui-focused fieldset": {
                                                borderColor: "black",
                                            },
                                        }}
                                        endAdornment={
                                            <IconButton
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setOpenProjectSelect((prev) => !prev);
                                                }}
                                                sx={{
                                                    padding: 0,
                                                    marginRight: "8px",
                                                    color: "black",
                                                }}
                                            >
                                                {openProjectSelect ? (
                                                    <FiChevronUp size={20} />
                                                ) : (
                                                    <FiChevronDown size={20} />
                                                )}
                                            </IconButton>
                                        }
                                    />
                                }
                                MenuProps={menuProps}
                                IconComponent={() => null}
                            >
                                {renderProjectMenuItems()}
                            </Select>
                        </FormControl>
                    </div>

                    <div className="md:col-span-8">
                        <label className="font-semibold block mb-1">Select Clients</label>
                        <FormControl fullWidth size="medium">
                            <Select
                                multiple
                                displayEmpty
                                open={openClientSelect}
                                value={notifyFormData.clientids}
                                onChange={handleNotifyClientChange}
                                onOpen={() => setOpenClientSelect(true)}
                                onClose={() => setOpenClientSelect(false)}
                                renderValue={renderNotifyClientValue}
                                input={
                                    <OutlinedInput
                                        sx={{
                                            backgroundColor: "#F3F8FB",
                                            borderRadius: "8px",
                                            padding: "3px",
                                            "& fieldset": {
                                                borderColor: "black",
                                                borderWidth: "2px",
                                            },
                                            "&:hover fieldset": {
                                                borderColor: "black",
                                            },
                                            "&.Mui-focused fieldset": {
                                                borderColor: "black",
                                            },
                                        }}
                                        endAdornment={
                                            <IconButton
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setOpenClientSelect((prev) => !prev);
                                                }}
                                                sx={{
                                                    padding: 0,
                                                    marginRight: "8px",
                                                    color: "black",
                                                }}
                                            >
                                                {openClientSelect ? (
                                                    <FiChevronUp size={20} />
                                                ) : (
                                                    <FiChevronDown size={20} />
                                                )}
                                            </IconButton>
                                        }
                                    />
                                }
                                MenuProps={menuProps}
                                IconComponent={() => null}
                            >
                                {renderClientMenuItems(notifyFormData.clientids)}
                            </Select>
                        </FormControl>

                        {projWiseClientsLoading && (
                            <div className="flex items-center mt-2 text-blue-600">
                                <CircularProgress size={16} className="mr-2" />
                                <span className="text-sm">Loading clients...</span>
                            </div>
                        )}

                        {projWiseClientsError && (
                            <div className="mt-2 text-red-600 text-sm">
                                Error: {projWiseClientsError}
                            </div>
                        )}

                        {notifyFormData.projectid &&
                            !projWiseClientsLoading &&
                            !projWiseClientsError && (
                                <div className="mt-2 text-gray-600 text-sm">
                                    {clients.length} client(s) available •{" "}
                                    {notifyFormData.clientids.length} selected
                                </div>
                            )}
                    </div>
                </div>

                {/* Display Selected Clients */}
                {notifyFormData.clientids.length > 0 && (
                    <div className="mb-6 bg-blue-50 border border-blue-200 p-4 rounded-lg">
                        <h4 className="font-semibold text-blue-900 mb-2 border-b border-blue-200 pb-2">
                            Selected Clients ({notifyFormData.clientids.length})
                        </h4>
                        <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                            {notifyFormData.clientids.map(id => {
                                const c = clients.find(client => client.id === id);
                                return c ? (
                                    <span key={id} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium border border-blue-300">
                                        {c.name}
                                    </span>
                                ) : null;
                            })}
                        </div>
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-4">
                    <div className="md:col-span-4">
                        <label className="font-semibold block mb-1">EXEL ID</label>
                        <input
                            type="text"
                            placeholder="EXEL ID"
                            readOnly
                            className="bg-gray-200 text-gray-700 cursor-not-allowed w-full p-3 border-2 border-black rounded"
                            value={uploadFormData.exelId}
                            onChange={(e) =>
                                setUploadFormData((prev) => ({
                                    ...prev,
                                    exelId: e.target.value,
                                }))
                            }
                        />
                    </div>
                    <div className="md:col-span-4">
                        <label className="font-semibold block mb-1">Select File (.xls, .xlsx)</label>
                        <input
                            id="exel-file-input"
                            type="file"
                            accept=".xls,.xlsx"
                            className="bg-[#F3F8FB] w-full p-2 border-2 border-black rounded"
                            onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                    setUploadFormData((prev) => ({
                                        ...prev,
                                        uploadFile: file,
                                        exelName: file.name
                                    }));
                                }
                            }}
                        />
                    </div>
                    <div className="md:col-span-4">
                        <label className="font-semibold block mb-1">EXEL Name</label>
                        <input
                            type="text"
                            placeholder="EXEL Name"
                            className="bg-[#F3F8FB] w-full p-3 border-2 border-black rounded"
                            value={uploadFormData.exelName}
                            onChange={(e) =>
                                setUploadFormData((prev) => ({
                                    ...prev,
                                    exelName: e.target.value,
                                }))
                            }
                        />
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                    <div className="md:col-span-12 flex justify-end space-x-2">
                        {editId && (
                            <button
                                onClick={handleCancel}
                                className="bg-gray-400 text-white py-2 px-4 rounded-md hover:bg-gray-500 transition-colors"
                            >
                                CANCEL
                            </button>
                        )}
                        <button
                            onClick={handleUploadExel}
                            disabled={uploadLoading || (!editId && !uploadFormData.uploadFile) || !uploadFormData.exelName || !uploadFormData.exelId || notifyFormData.clientids.length === 0}
                            className={`${uploadLoading || (!editId && !uploadFormData.uploadFile) || !uploadFormData.exelName || !uploadFormData.exelId || notifyFormData.clientids.length === 0
                                ? 'bg-gray-400 cursor-not-allowed'
                                : 'bg-[#122A47] hover:bg-[#0c2545]'
                                } text-white py-2 px-4 rounded-md transition-colors`}
                        >
                            {uploadLoading ? (
                                <span className="flex items-center">
                                    <CircularProgress size={20} className="mr-2 text-white" />
                                    {editId ? "Updating..." : "Uploading..."}
                                </span>
                            ) : (
                                editId ? "Update EXEL" : "Upload EXEL"
                            )}
                        </button>
                    </div>
                </div>

            </div>

            {/* Table Section */}
            <div className="rounded-xl p-6 shadow-lg bg-white">
                <div className="title mb-5">
                    <h3 className="text-xl font-bold main_title_page text-white px-4 py-2 inline-block rounded">
                        Uploaded EXEL Details
                    </h3>
                </div>
                <div className="mt-5">
                    <DynamicTable rows={rows} columns={columns} />
                </div>
            </div>

        </div>
    );
};

export default ExelUpdate;
